import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import photoService from '../../services/photoService';
import type {
  PhotoCategory,
  PhotoQuery,
  PhotoStats,
  PhotoUploadData,
  ProjectPhoto,
} from '../../types';

// Transform MongoDB photo document to frontend format
const transformPhoto = (photo: any): ProjectPhoto => {
  const transformObjectId = (value: any) =>
    typeof value === 'object' && value?.$oid ? value.$oid : value;

  const transformDate = (value: any) =>
    typeof value === 'object' && value?.$date ? value.$date : value;

  return {
    ...photo,
    photo_id: photo.photo_id,
    project_id: transformObjectId(photo.project_id),
    uploaded_by: transformObjectId(photo.uploaded_by),
    uploaded_at: transformDate(photo.uploaded_at),
    updated_at: transformDate(photo.updated_at),
    createdAt: transformDate(photo.createdAt),
    updatedAt: transformDate(photo.updatedAt),
  };
};

// Async thunks for API calls
export const fetchPhotos = createAsyncThunk(
  'photos/fetchPhotos',
  async ({ query, projectId }: { query: PhotoQuery; projectId?: string }, { getState }) => {
    // Prevent duplicate calls with same query if already loading
    const state = getState() as { photos: PhotosState };
    if (state.photos.loading && JSON.stringify(state.photos.query) === JSON.stringify(query)) {
      return {
        photos: state.photos.photos,
        currentPage: state.photos.currentPage,
        totalPages: state.photos.totalPages,
        totalPhotos: state.photos.totalPhotos,
      };
    }

    const response = await photoService.getPhotos(query, projectId);

    // Transform photos if they exist and normalize response structure
    if (response && response.photos && Array.isArray(response.photos)) {
      const transformedPhotos = response.photos.map(transformPhoto);
      return {
        photos: transformedPhotos,
        currentPage: (response as any).page || (response as any).currentPage || 1,
        totalPages:
          (response as any).totalPages ||
          Math.ceil(((response as any).total || 0) / (query.limit || 20)),
        totalPhotos: (response as any).total || (response as any).totalPhotos || 0,
      };
    }

    return { photos: [], totalPages: 0, currentPage: 1, totalPhotos: 0 };
  }
);

export const fetchPhotoStats = createAsyncThunk(
  'photos/fetchPhotoStats',
  async (projectId: string | undefined, { getState }) => {
    // Prevent duplicate calls if already loading
    const state = getState() as { photos: PhotosState };
    if (state.photos.statsLoading) {
      return state.photos.stats;
    }

    const response = await photoService.getPhotoStats(projectId);

    // Ensure stats structure matches PhotoStats interface
    return {
      total_photos: (response as any)?.total_photos || 0,
      by_category: (response as any)?.by_category || ({} as Record<PhotoCategory, number>),
      recent_uploads: (response as any)?.recent_uploads || 0,
      storage_used: (response as any)?.storage_used || 0,
    };
  }
);

export const uploadPhotos = createAsyncThunk(
  'photos/uploadPhotos',
  async ({
    uploadData,
    onProgress,
    projectId,
  }: {
    uploadData: PhotoUploadData;
    onProgress?: (progress: number) => void;
    projectId?: string;
  }) => {
    // Basic validation before upload
    if (!uploadData.files || uploadData.files.length === 0) {
      throw new Error('No files selected for upload');
    }

    if (uploadData.files.length > 10) {
      throw new Error('Maximum 10 files allowed per upload');
    }

    // Validate image files
    const invalidFiles = uploadData.files.filter(
      file => !file.type.startsWith('image/') || file.size > 10 * 1024 * 1024
    );

    if (invalidFiles.length > 0) {
      throw new Error(
        `Invalid files detected: ${invalidFiles.length} files are either not images or exceed 10MB`
      );
    }

    return await photoService.uploadPhotos(uploadData, onProgress, projectId);
  }
);

export const deletePhoto = createAsyncThunk(
  'photos/deletePhoto',
  async ({ photoId, projectId }: { photoId: string; projectId?: string }) => {
    return await photoService.deletePhoto(photoId, projectId);
  }
);

export const bulkDeletePhotos = createAsyncThunk(
  'photos/bulkDeletePhotos',
  async ({ photoIds, projectId }: { photoIds: string[]; projectId?: string }) => {
    return await photoService.bulkDeletePhotos(photoIds, projectId);
  }
);

export const fetchDropdownData = createAsyncThunk('photos/fetchDropdownData', async () => {
  // Fetch all dropdown data in parallel
  const [categoriesResponse, projectsResponse, sortOptionsResponse] = await Promise.all([
    photoService.getPhotoCategories(),
    photoService.getProjectsForDropdown(),
    photoService.getSortOptions(),
  ]);

  return {
    categories: categoriesResponse,
    projects: projectsResponse,
    sortOptions: sortOptionsResponse,
  };
});

export const fetchPermissions = createAsyncThunk(
  'photos/fetchPermissions',
  async (_projectId?: string) => {
    const response = await photoService.getPhotoPermissions();
    return response || {};
  }
);

// Initial state
interface PhotosState {
  // Data
  photos: ProjectPhoto[];
  stats: PhotoStats;
  categories: { value: string; label: string; color: string }[];
  projects: { value: string; label: string }[];
  sortOptions: { value: string; label: string }[];
  permissions: any;

  // Pagination
  currentPage: number;
  totalPages: number;
  totalPhotos: number;

  // Loading states
  loading: boolean;
  filterLoading: boolean;
  statsLoading: boolean;
  dropdownLoading: boolean;
  uploading: boolean;
  deleting: boolean;

  // Upload progress
  uploadProgress: number;

  // Query state
  query: PhotoQuery;

  // Upload form state
  uploadData: PhotoUploadData;

  // UI state
  selectedPhotos: string[]; // Changed from Set to Array for Redux serialization
  viewMode: 'gallery' | 'list' | 'timeline';
  showUploadModal: boolean;

  // Location state
  detectingLocation: boolean;
  locationPermission: 'granted' | 'denied' | 'prompt' | null;

  // Error state
  error: string | null;
}

const initialState: PhotosState = {
  // Data
  photos: [],
  stats: {
    total_photos: 0,
    by_category: {} as Record<PhotoCategory, number>,
    recent_uploads: 0,
    storage_used: 0,
  },
  categories: [],
  projects: [],
  sortOptions: [],
  permissions: {},

  // Pagination
  currentPage: 1,
  totalPages: 0,
  totalPhotos: 0,

  // Loading states
  loading: false,
  filterLoading: false,
  statsLoading: false,
  dropdownLoading: false,
  uploading: false,
  deleting: false,

  // Upload progress
  uploadProgress: 0,

  // Query state
  query: {
    page: 1,
    limit: 20,
    search: '',
    category: undefined,
    project_id: '',
    location: '',
    sort_by: 'uploaded_at',
    sort_order: 'desc',
    date_from: '',
    date_to: '',
    uploaded_by: '',
  },

  // Upload form state
  uploadData: {
    project_id: '',
    files: [],
    title: '',
    description: '',
    category: 'progress' as PhotoCategory,
    tags: [],
    location: {
      site: '',
      building: '',
      floor: '',
      room: '',
      gps: '',
    },
    number_on_image: '',
  },

  // UI state
  selectedPhotos: [], // Changed from Set to Array
  viewMode: 'gallery',
  showUploadModal: false,

  // Location state
  detectingLocation: false,
  locationPermission: null,

  // Error state
  error: null,
};

const photosSlice = createSlice({
  name: 'photos',
  initialState,
  reducers: {
    // Query actions
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.query.search = action.payload;
      state.query.page = 1; // Reset to first page when searching
    },
    setSelectedCategory: (state, action: PayloadAction<PhotoCategory | undefined>) => {
      state.query.category = action.payload;
      state.query.page = 1;
    },
    setSelectedProject: (state, action: PayloadAction<string>) => {
      state.query.project_id = action.payload;
      state.query.page = 1;
    },
    setDateRange: (state, action: PayloadAction<{ from: string; to: string }>) => {
      state.query.date_from = action.payload.from;
      state.query.date_to = action.payload.to;
      state.query.page = 1;
    },
    setSelectedLocation: (state, action: PayloadAction<string>) => {
      state.query.location = action.payload;
      state.query.page = 1;
    },
    setUploadedBy: (state, action: PayloadAction<string>) => {
      state.query.uploaded_by = action.payload;
      state.query.page = 1;
    },
    setSortBy: (
      state,
      action: PayloadAction<'uploaded_at' | 'category' | 'tagging_id' | 'title'>
    ) => {
      state.query.sort_by = action.payload;
      state.query.page = 1;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.query.sort_order = action.payload;
      state.query.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.query.page = action.payload;
    },
    resetFilters: state => {
      state.query = {
        ...initialState.query,
        project_id: state.query.project_id, // Preserve project_id for project-specific views
      };
    },

    // Upload form actions
    updateUploadData: (state, action: PayloadAction<Partial<PhotoUploadData>>) => {
      state.uploadData = { ...state.uploadData, ...action.payload };
    },
    setUploadFiles: (state, action: PayloadAction<File[]>) => {
      state.uploadData.files = action.payload;
    },
    resetUploadForm: state => {
      state.uploadData = {
        ...initialState.uploadData,
        project_id: state.uploadData.project_id, // Preserve project_id
      };
      state.uploadProgress = 0;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },

    // Selection actions
    togglePhotoSelection: (state, action: PayloadAction<string>) => {
      const photoId = action.payload;
      const currentIndex = state.selectedPhotos.indexOf(photoId);
      if (currentIndex >= 0) {
        // Remove if already selected
        state.selectedPhotos.splice(currentIndex, 1);
      } else {
        // Add if not selected
        state.selectedPhotos.push(photoId);
      }
    },
    selectAllPhotos: state => {
      state.selectedPhotos = state.photos.map(photo => photo.photo_id);
    },
    clearSelection: state => {
      state.selectedPhotos = [];
    },

    // UI actions
    setViewMode: (state, action: PayloadAction<'gallery' | 'list' | 'timeline'>) => {
      state.viewMode = action.payload;
    },
    setShowUploadModal: (state, action: PayloadAction<boolean>) => {
      state.showUploadModal = action.payload;
    },

    // Location actions
    setDetectingLocation: (state, action: PayloadAction<boolean>) => {
      state.detectingLocation = action.payload;
    },
    setLocationPermission: (
      state,
      action: PayloadAction<'granted' | 'denied' | 'prompt' | null>
    ) => {
      state.locationPermission = action.payload;
    },
    updateLocationGPS: (state, action: PayloadAction<string>) => {
      state.uploadData.location.gps = action.payload;
    },

    // Error actions
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    // Fetch photos
    builder
      .addCase(fetchPhotos.pending, (state, action) => {
        // Check if this is a filter operation based on the meta
        const isFilter =
          action.meta.arg.query.page === 1 &&
          (action.meta.arg.query.search ||
            action.meta.arg.query.category ||
            action.meta.arg.query.project_id ||
            action.meta.arg.query.date_from ||
            action.meta.arg.query.date_to);

        if (isFilter) {
          state.filterLoading = true;
        } else {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchPhotos.fulfilled, (state, action) => {
        state.loading = false;
        state.filterLoading = false;
        state.photos = action.payload.photos;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.totalPhotos = action.payload.totalPhotos;
        state.error = null;
      })
      .addCase(fetchPhotos.rejected, (state, action) => {
        state.loading = false;
        state.filterLoading = false;
        state.error = action.error.message || 'Failed to fetch photos';
      });

    // Fetch stats
    builder
      .addCase(fetchPhotoStats.pending, state => {
        state.statsLoading = true;
      })
      .addCase(fetchPhotoStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchPhotoStats.rejected, state => {
        state.statsLoading = false;
      });

    // Upload photos
    builder
      .addCase(uploadPhotos.pending, state => {
        state.uploading = true;
        state.error = null;
      })
      .addCase(uploadPhotos.fulfilled, state => {
        state.uploading = false;
        state.uploadProgress = 0;
        // Reset form after successful upload
        state.uploadData = {
          ...initialState.uploadData,
          project_id: state.uploadData.project_id,
        };
      })
      .addCase(uploadPhotos.rejected, (state, action) => {
        state.uploading = false;
        state.uploadProgress = 0;
        state.error = action.error.message || 'Failed to upload photos';
      });

    // Delete photo
    builder
      .addCase(deletePhoto.pending, state => {
        state.deleting = true;
      })
      .addCase(deletePhoto.fulfilled, state => {
        state.deleting = false;
      })
      .addCase(deletePhoto.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.error.message || 'Failed to delete photo';
      });

    // Bulk delete photos
    builder
      .addCase(bulkDeletePhotos.pending, state => {
        state.deleting = true;
      })
      .addCase(bulkDeletePhotos.fulfilled, state => {
        state.deleting = false;
        state.selectedPhotos = []; // Clear selection after bulk delete
      })
      .addCase(bulkDeletePhotos.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.error.message || 'Failed to delete photos';
      });

    // Fetch dropdown data
    builder
      .addCase(fetchDropdownData.pending, state => {
        state.dropdownLoading = true;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        state.categories = action.payload.categories;
        state.projects = action.payload.projects;
        state.sortOptions = action.payload.sortOptions;
      })
      .addCase(fetchDropdownData.rejected, state => {
        state.dropdownLoading = false;
      });

    // Fetch permissions
    builder.addCase(fetchPermissions.fulfilled, (state, action) => {
      state.permissions = action.payload;
    });
  },
});

export const {
  // Query actions
  setSearchTerm,
  setSelectedCategory,
  setSelectedProject,
  setDateRange,
  setSelectedLocation,
  setUploadedBy,
  setSortBy,
  setSortOrder,
  setPage,
  resetFilters,

  // Upload form actions
  updateUploadData,
  setUploadFiles,
  resetUploadForm,
  setUploadProgress,

  // Selection actions
  togglePhotoSelection,
  selectAllPhotos,
  clearSelection,

  // UI actions
  setViewMode,
  setShowUploadModal,

  // Location actions
  setDetectingLocation,
  setLocationPermission,
  updateLocationGPS,

  // Error actions
  setError,
  clearError,
} = photosSlice.actions;

export default photosSlice.reducer;
